import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, Star, Mail, Phone, Globe, Instagram, Facebook, Youtube, ExternalLink, Share2, Copy, Check, Calendar, Clock, Euro, Languages, Linkedin, Twitter, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { getLocalizedText, formatPrice, formatDate } from '../utils/helpers';
import SocialShare from '../components/SocialShare';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Language options
const LANGUAGE_OPTIONS = [
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
];

export default function PublicProfilePage() {
  const { identifier } = useParams();
  const { t, i18n } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language || 'es');
  const [showLangMenu, setShowLangMenu] = useState(false);
  
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Handle language change
  const handleLanguageChange = (langCode) => {
    setSelectedLanguage(langCode);
    i18n.changeLanguage(langCode);
    setShowLangMenu(false);
  };
  
  // Use selected language for content
  const language = selectedLanguage;
  
  useEffect(() => {
    loadProfile();
  }, [identifier]);
  
  const loadProfile = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/profile/${identifier}`);
      if (response.data.found) {
        setProfile(response.data);
      } else {
        setError('not_found');
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      setError('error');
    }
    setIsLoading(false);
  };
  
  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-cream to-white">
        <div className="animate-pulse text-gold text-lg">{t('common.loading', 'Cargando...')}</div>
      </div>
    );
  }
  
  if (error || !profile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-cream to-white px-4">
        <h1 className="text-2xl font-serif text-primary mb-4">{t('profile.notFound', 'Perfil no encontrado')}</h1>
        <p className="text-secondary mb-6">{t('profile.notFoundDesc', 'El perfil que buscas no existe o no está disponible.')}</p>
        <Link to="/">
          <Button className="bg-gold hover:bg-gold/90 text-white" style={{ backgroundColor: '#D4A574' }}>
            {t('common.backHome', 'Volver al inicio')}
          </Button>
        </Link>
      </div>
    );
  }
  
  const { user, teacher, school, services, courses } = profile;
  const entity = teacher || school;
  const isTeacher = !!teacher;
  const googleRating = entity?.google_business_rating;
  const hasVerifiedRating = googleRating?.is_verified && googleRating?.google_rating > 0;
  
  // Social links
  const socialLinks = entity?.social_links || {};
  const hasSocialLinks = Object.values(socialLinks).some(v => v);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-cream to-white" style={{ background: 'linear-gradient(to bottom right, #FDFBF7, #F5EFE6)' }}>
      {/* Header with gradient */}
      <div className="relative h-48 sm:h-64 bg-gradient-to-r from-gold/80 to-gold" style={{ background: 'linear-gradient(135deg, #D4A574 0%, #C49A6C 50%, #B8956A 100%)' }}>
        <div className="absolute inset-0 bg-black/10"></div>
        
        {/* Language selector & Share button */}
        <div className="absolute top-4 right-4 flex gap-2">
          {/* Language Selector */}
          <div className="relative">
            <button
              onClick={() => setShowLangMenu(!showLangMenu)}
              className="flex items-center gap-2 px-3 py-2 bg-white/90 hover:bg-white rounded-full text-sm font-medium text-gray-700 shadow-lg transition-all"
            >
              <Languages className="w-4 h-4" />
              <span>{LANGUAGE_OPTIONS.find(l => l.code === selectedLanguage)?.flag}</span>
              <span className="hidden sm:inline">{LANGUAGE_OPTIONS.find(l => l.code === selectedLanguage)?.label}</span>
            </button>
            
            {/* Language Dropdown */}
            {showLangMenu && (
              <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-xl overflow-hidden z-50 min-w-[140px]">
                {LANGUAGE_OPTIONS.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                      selectedLanguage === lang.code ? 'bg-gold/10 text-gold font-medium' : 'text-gray-700'
                    }`}
                    style={selectedLanguage === lang.code ? { color: '#D4A574' } : {}}
                  >
                    <span className="text-lg">{lang.flag}</span>
                    <span>{lang.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Copy Link Button */}
          <button
            onClick={handleCopyLink}
            className="flex items-center gap-2 px-3 py-2 bg-white/90 hover:bg-white rounded-full text-sm font-medium text-gray-700 shadow-lg transition-all"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-green-600" />
                <span className="hidden sm:inline">{t('common.copied', 'Copiado!')}</span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                <span className="hidden sm:inline">{t('common.copyLink', 'Copiar enlace')}</span>
              </>
            )}
          </button>
        </div>
      </div>
      
      {/* Profile Card */}
      <div className="max-w-4xl mx-auto px-4 -mt-16 sm:-mt-20 relative z-10 pb-12">
        {/* Avatar - positioned outside the card to avoid clipping */}
        <div className="flex justify-center mb-4">
          <div className="relative">
            <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-full border-4 border-white shadow-xl overflow-hidden bg-cream">
              {user.avatar ? (
                <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
              ) : entity?.images?.[0] ? (
                <img src={entity.images[0]} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gold/20 to-gold/10">
                  <span className="text-4xl font-serif text-gold">{user.name?.charAt(0) || '?'}</span>
                </div>
              )}
            </div>
            
            {/* Role badge */}
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
              <Badge className="bg-gold text-white px-3 py-1" style={{ backgroundColor: '#D4A574' }}>
                {isTeacher ? t('teacher.professional', 'Profesional') : t('school.school', 'Escuela')}
              </Badge>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Profile Header */}
          <div className="px-6 pt-4 pb-4 text-center">
            
            {/* Name */}
            <h1 className="font-serif text-2xl sm:text-3xl font-bold text-primary mb-2">{user.name}</h1>
            
            {/* Location */}
            {entity?.location?.city && (
              <div className="flex items-center justify-center gap-1 text-secondary mb-3">
                <MapPin className="w-4 h-4" />
                <span>{entity.location.city}, {entity.location.province || entity.location.country}</span>
              </div>
            )}
            
            {/* Google Rating */}
            {hasVerifiedRating && (
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-full mb-4">
                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                <span className="text-lg font-bold text-gray-800">{googleRating.google_rating.toFixed(1)}</span>
                <span className="text-gray-500">({googleRating.google_reviews_count} {t('googleRating.reviews', 'reseñas')})</span>
                {googleRating.google_maps_url && (
                  <a href={googleRating.google_maps_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}
            
            {/* Bio */}
            {entity?.bio && (
              <p className="text-secondary max-w-2xl mx-auto leading-relaxed whitespace-pre-wrap text-left px-2">
                {getLocalizedText(entity.bio, language)}
              </p>
            )}
            {entity?.description && (
              <p className="text-secondary max-w-2xl mx-auto leading-relaxed whitespace-pre-wrap text-left px-2">
                {getLocalizedText(entity.description, language)}
              </p>
            )}
          </div>
          
          {/* Contact Info Section */}
          {(entity?.phone || entity?.email || entity?.website) && (
            <div className="border-t border-gray-100 px-6 py-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 text-center">
                {t('profile.contactInfo', 'Contacto')}
              </h3>
              <div className="flex flex-wrap justify-center gap-3">
                {entity?.phone && (
                  <a 
                    href={`tel:${entity.phone}`}
                    className="flex items-center gap-2 px-4 py-2 bg-green-50 hover:bg-green-100 text-green-700 rounded-full transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    <span className="text-sm font-medium">{entity.phone}</span>
                  </a>
                )}
                {entity?.email && (
                  <a 
                    href={`mailto:${entity.email}`}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-full transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    <span className="text-sm font-medium">{entity.email}</span>
                  </a>
                )}
                {entity?.website && (
                  <a 
                    href={entity.website.startsWith('http') ? entity.website : `https://${entity.website}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-full transition-colors"
                  >
                    <Globe className="w-4 h-4" />
                    <span className="text-sm font-medium">{t('profile.visitWebsite', 'Visitar web')}</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          )}
          
          {/* Social Links */}
          {hasSocialLinks && (
            <div className="border-t border-gray-100 px-6 py-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 text-center">
                {t('profile.socialMedia', 'Redes Sociales')}
              </h3>
              <div className="flex flex-wrap justify-center gap-3">
                {socialLinks.instagram && (
                  <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" 
                     className="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white hover:opacity-90 hover:scale-110 transition-all">
                    <Instagram className="w-5 h-5" />
                  </a>
                )}
                {socialLinks.facebook && (
                  <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer"
                     className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-600 text-white hover:opacity-90 hover:scale-110 transition-all">
                    <Facebook className="w-5 h-5" />
                  </a>
                )}
                {socialLinks.youtube && (
                  <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer"
                     className="w-10 h-10 flex items-center justify-center rounded-full bg-red-600 text-white hover:opacity-90 hover:scale-110 transition-all">
                    <Youtube className="w-5 h-5" />
                  </a>
                )}
                {socialLinks.linkedin && (
                  <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer"
                     className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-700 text-white hover:opacity-90 hover:scale-110 transition-all">
                    <Linkedin className="w-5 h-5" />
                  </a>
                )}
                {socialLinks.twitter && (
                  <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer"
                     className="w-10 h-10 flex items-center justify-center rounded-full bg-sky-500 text-white hover:opacity-90 hover:scale-110 transition-all">
                    <Twitter className="w-5 h-5" />
                  </a>
                )}
                {socialLinks.tiktok && (
                  <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer"
                     className="w-10 h-10 flex items-center justify-center rounded-full bg-black text-white hover:opacity-90 hover:scale-110 transition-all">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                    </svg>
                  </a>
                )}
                {socialLinks.telegram && (
                  <a href={socialLinks.telegram} target="_blank" rel="noopener noreferrer"
                     className="w-10 h-10 flex items-center justify-center rounded-full bg-sky-400 text-white hover:opacity-90 hover:scale-110 transition-all">
                    <MessageCircle className="w-5 h-5" />
                  </a>
                )}
                {socialLinks.website && (
                  <a href={socialLinks.website} target="_blank" rel="noopener noreferrer"
                     className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-700 text-white hover:opacity-90 hover:scale-110 transition-all">
                    <Globe className="w-5 h-5" />
                  </a>
                )}
              </div>
            </div>
          )}
          
          {/* Share Section */}
          <div className="border-t px-6 py-5">
            <div className="text-center mb-3">
              <p className="text-sm text-secondary">
                {t('profile.shareInvite', '¿Te ha gustado este perfil? ¡Compártelo con tus amigos!')}
              </p>
            </div>
            <SocialShare 
              url={window.location.href}
              title={`${user.name} - Tantra Map`}
              description={entity?.bio ? getLocalizedText(entity.bio, language) : `Descubre los servicios de ${user.name}`}
            />
          </div>
        </div>
        
        {/* Services Section */}
        {services.length > 0 && (
          <div className="mt-8">
            <h2 className="font-serif text-xl font-bold text-primary mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center">
                <Star className="w-4 h-4 text-gold" style={{ color: '#D4A574' }} />
              </span>
              {t('nav.services', 'Sesiones')} ({services.length})
            </h2>
            
            <div className="grid gap-4 sm:grid-cols-2">
              {services.map(service => (
                <Link key={service.id} to={`/service/${service.id}`} className="block">
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                    <div className="flex">
                      {/* Image */}
                      <div className="w-24 h-24 sm:w-32 sm:h-32 flex-shrink-0 bg-cream overflow-hidden">
                        {service.images?.[0] ? (
                          <img src={service.images[0]} alt={getLocalizedText(service.title, language)} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Star className="w-8 h-8 text-gold/50" style={{ color: '#D4A574' }} />
                          </div>
                        )}
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 p-3 sm:p-4">
                        <h3 className="font-semibold text-primary text-sm sm:text-base line-clamp-1">
                          {getLocalizedText(service.title, language)}
                        </h3>
                        <p className="text-xs sm:text-sm text-secondary line-clamp-2 mt-1">
                          {getLocalizedText(service.description, language)}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-2 text-xs text-secondary">
                            <Clock className="w-3 h-3" />
                            <span>{service.duration_minutes} min</span>
                          </div>
                          <span className="font-bold text-gold" style={{ color: '#D4A574' }}>
                            {formatPrice(service.price, service.currency)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
        
        {/* Courses Section */}
        {courses.length > 0 && (
          <div className="mt-8">
            <h2 className="font-serif text-xl font-bold text-primary mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                <Calendar className="w-4 h-4 text-emerald-600" />
              </span>
              {t('nav.courses', 'Formación y Eventos')} ({courses.length})
            </h2>
            
            <div className="grid gap-4 sm:grid-cols-2">
              {courses.map(course => (
                <Link key={course.id} to={`/course/${course.id}`} className="block">
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                    <div className="flex">
                      {/* Image */}
                      <div className="w-24 h-24 sm:w-32 sm:h-32 flex-shrink-0 bg-cream overflow-hidden">
                        {course.images?.[0] ? (
                          <img src={course.images[0]} alt={getLocalizedText(course.title, language)} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Calendar className="w-8 h-8 text-emerald-400" />
                          </div>
                        )}
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 p-3 sm:p-4">
                        <h3 className="font-semibold text-primary text-sm sm:text-base line-clamp-1">
                          {getLocalizedText(course.title, language)}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          {course.is_online && <Badge variant="outline" className="text-xs">{t('course.types.online', 'Online')}</Badge>}
                          {course.is_residential && <Badge variant="outline" className="text-xs border-green-500 text-green-600">{t('course.residential', 'Residencial')}</Badge>}
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-1 text-xs text-secondary">
                            <Calendar className="w-3 h-3" />
                            <span>{course.start_date ? formatDate(course.start_date) : t('course.flexibleDates', 'Fechas flexibles')}</span>
                          </div>
                          <span className="font-bold text-gold" style={{ color: '#D4A574' }}>
                            {formatPrice(course.price, course.currency)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
        
        {/* Empty State */}
        {services.length === 0 && courses.length === 0 && (
          <div className="mt-8 bg-white rounded-xl shadow-sm p-8 text-center">
            <p className="text-secondary">{t('profile.noServicesYet', 'Este profesional aún no ha publicado servicios o formaciones.')}</p>
          </div>
        )}
        
        {/* Back to Home */}
        <div className="mt-8 text-center">
          <Link to="/">
            <Button variant="outline" className="border-gold text-gold hover:bg-gold/10" style={{ borderColor: '#D4A574', color: '#D4A574' }}>
              {t('profile.exploreMore', 'Explorar más en Tantra Map')}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
